import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, MessageSquare, Inbox, Target } from 'lucide-react';
import { useDmdilly } from './dmdilly-context';
import { getCreatorProfile } from './api';
import ProfileCard from './ui/profile-card';
import LoadingSpinner from './ui/loading-spinner';
import ErrorMessage from './ui/error-message';
import { fetchProfileData } from '../../utils';

export default function CreatorFocus({
  onStartInterview,
  onViewInbox,
  onPreviewFanPage,
  creatorIdOverride,
}) {
  const { apiBaseUrl, creatorId: contextCreatorId } = useDmdilly();
  const creatorId = creatorIdOverride || contextCreatorId;

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProfile = async () => {
    setLoading(true);
    setError(null);

    const guideData = await fetchProfileData();
    const { data, error: apiError } = await getCreatorProfile(apiBaseUrl, guideData.guide._id);

    if (apiError) {
      setError(apiError);
      setLoading(false);
      return;
    }

    setProfile(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchProfile();
  }, [creatorId]);

  if (loading) {
    return <LoadingSpinner message="Loading your focus..." />;
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={fetchProfile} />;
  }

  if (!profile) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Target size={48} className="text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-800 mb-2">
          No Focus Set Yet
        </h3>
        <p className="text-gray-500 mb-6 max-w-sm">
          Complete a quick interview to tell us what kind of clients you want to
          prioritize right now.
        </p>
        {onStartInterview && (
          <button
            onClick={onStartInterview}
            className="px-6 py-3 bg-boggler-teal text-white rounded-xl hover:bg-boggler-blue transition-colors font-medium"
          >
            Start Interview
          </button>
        )}
      </div>
    );
  }  
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Your Current Focus</h2>
          <p className="text-sm text-gray-500">What you're looking for right now</p>
        </div>
        <button
          onClick={fetchProfile}
          className="p-2 text-gray-500 hover:text-boggler-teal hover:bg-boggler-teal/10 rounded-lg transition-colors"
          title="Refresh"
        >
          <RefreshCw size={20} />
        </button>
      </div>

      {/* Profile Card */}
      <ProfileCard profile={profile.data} />

    </div>
  );
}
